import React from "react";

const IdentityVisionVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
      This lesson separates three things people mix up: vision, desire, and values. Desire is the next title, vision is the impact you want to create, and values are the rules you refuse to break while getting there. Through Niel and Rhea, it shows how different value sets fit different business moments, and why promotions signal where the company expects impact next. The takeaway is to align your actions and brand to the values baked into the role you want.
      </p>
    </div>
  );

  const transcriptHtml = `
    <p>Values don't just guide decisions, they feed your <strong>personal vision</strong>. But here's where people usually get confused: vision and desire are not the same thing.</p>

    <p><strong>Desire</strong> is the position you want.</p>
    <p>The title. The promotion. The shiny badge.</p>

    <p><strong>Vision</strong> is the impact you want to create.</p>
    <p>The future you're trying to build, not the chair you want to sit on.</p>

    <p>And <strong>values</strong>?</p>
    <p>Values are the compass that decides the path you're willing to take to get there.</p>
    <p>They are the <em>why</em> behind the <em>how</em>.</p>

    <p>For instance: If your vision is to change your company's culture someday, that's the impact.</p>
    <p>To do that, you may desire to reach the CXO level. But first, you need to become a Senior Manager.</p>

    <p><strong>Here's the connection:</strong></p>
    <ul style="margin-left: 20px;">
      <li>Vision = the change you want to create</li>
      <li>Desire = the career step you want next</li>
      <li>Values = the rules you refuse to break on the way</li>
    </ul>

    <p>If ethics is a core value for you, it dictates the route you take. Because if you chase your desire in a way that breaks your values, you won't just feel tired—you'll feel internally misaligned.</p>

    <p>And when your values and your path don't match, the difficulty level multiplies. Everything feels like a shortcut that somehow became the long route.</p>

    <p><strong>The criteria is when paths look similar, your values break the tie.</strong></p>

    <p>Let me illustrate.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">Meet Niel and Rhea</h3>
    <p>two senior product managers working in the same company with the same experience:</p>

    <p><strong>Rhea's values are innovation, ownership, and velocity.</strong> She experiments, takes smart, reversible risks, and tells clear, outcome-driven stories. She's wired for creation, thriving where nothing exists yet. She walks into blank pages, bets on ideas, and builds what isn't there. She turns possibility into proof. Her focus? New, ambiguous opportunities and 0-to-1 journeys.</p>

    <p><strong>Niel's values are reliability, teamwork, and customer trust.</strong> He thrives where systems already exist and it's time to scale. He strengthens resilience, reduces incidents, mentors the team, and optimizes what works. He shines in 1-to-10 journeys, taking proven ideas and making them even better.</p>

    <p>Both are promotable, but the company bets on the person whose values match the business vision at that moment. <strong>Promotions are never random; they are signals of where impact will come from.</strong></p>

    <p>Aligning your personal values with your stakeholders, i.e. your manager, finance, or key partners vision is equally critical. Organizations reward behaviors that reflect their priorities.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">How do I turn misalignment into alignment?</h3>
    <p>Think about the role you're aiming for. Every role has a set of values baked into it. A sum total of these values should be your vision.</p>
    <p>If your next role requires leadership under ambiguity, focus on ownership, courage, and experimentation.</p>
    <p>If it's a platform or scale role, highlight reliability, mentorship, and execution discipline.</p>
    <p>Align your actions and your personal brand to the values that will make you a natural fit for that role.</p>

    <p>A growing tree with values as roots, vision as the trunk, and promotions or impact as the leaves/fruit.</p>

    <p><strong>Vision without values drifts. Values without vision stall.</strong> Together, they create focus, momentum, and trust.</p>

    <p><strong>Where do you think your values and vision intersect?</strong></p>
  `;

  const transcript = (
    <div
      className="pt-2 pb-6 text-body leading-7 text-[#484848] max-w-2xl"
      dangerouslySetInnerHTML={{ __html: transcriptHtml }}
    />
  );

  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default IdentityVisionVideo;
